/**
 * Collapse a rig's saved contacts to the DISTINCT CANDIDATES an ambiguity test
 * should count.
 *
 * ── THE BUG THIS EXISTS FOR ────────────────────────────────────────────────
 * NewJobScreen auto-fills the contact only when a (client, rig) pair matches
 * EXACTLY ONE saved contact — the reasonable rule that several contacts mean
 * "let the user pick". It counted ROWS. Measured against production
 * 2026-09-09, over every (company, customer, rig) group holding more than one
 * contact row:
 *
 *   rig            rows   distinct addresses
 *   ensign 418      3      1     ← one shared rig mailbox, three named hands
 *   bonanza 4       3      1     ← same
 *   bonanza 8       2      1     ← same
 *   savanna 437     2      1     ← same
 *   excaliber 20    3      2     ← genuinely two addresses
 *   global 21       2      2     ← genuinely two addresses
 *   pd 545          2      0     ← two hands, NO address on either
 *
 * FOUR OF THE SIX ADDRESSED RIGS ARE ONE MAILBOX. On those, "several contacts"
 * was never a conflict — the office had recorded the same rig address against
 * each hand who works it, which is the tenant's own convention working exactly
 * as intended. Refusing to auto-fill there is a refusal INVENTED BY COUNTING
 * ROWS: there is nothing for the user to disambiguate, because every choice
 * leads to the same address.
 *
 * The cost was already written down beside the rule it broke, in NewJobScreen's
 * note on the rig-change clear: "the auto-fill only refills when the
 * (client, rig) pair matches EXACTLY ONE saved contact, so it silently failed
 * for every client with several — 85% of contacts survived where one contact
 * could refill them and 29% where three could not."
 *
 * ── THE RULE ───────────────────────────────────────────────────────────────
 * Dedupe on the NORMALISED ADDRESS, then count. This is the same
 * address-not-row distinction `refuseSuppressedSend` already makes on the API
 * side, and for the same reason it gives there: "the same dead address usually
 * sits in several sources at once, and naming it once per source reads as three
 * different problems."
 *
 * ── ROWS WITH NO ADDRESS DO NOT COLLAPSE ───────────────────────────────────
 * `pd 545` is why. Two contacts with no email are not one contact reached at
 * one mailbox — they are two different people, and there is no address to tell
 * them apart BY. Keying them both on '' would merge two strangers and auto-fill
 * an arbitrary one of their names. Each addressless row stays its own
 * candidate, so that rig stays ambiguous, correctly.
 *
 * ── A COLLAPSED GROUP CONTRIBUTES ONLY WHAT IT AGREES ON ───────────────────
 * The group agrees on the address by construction. It usually does NOT agree on
 * the name — that is the whole shape: one mailbox, several named hands. Filling
 * an arbitrary one of those names onto the job would invent a fact. So a field
 * on which the group disagrees comes back BLANK and the office types it, which
 * is what they do today anyway. This is strictly more than the current
 * behaviour (which fills nothing at all) and never less true.
 *
 * ── WHAT THIS IS NOT FOR ───────────────────────────────────────────────────
 * The "Choose from saved contacts" dropdown still lists every ROW. Three named
 * hands on one mailbox is useful there — the office is picking a PERSON. Only
 * the ambiguity TEST dedupes, because only it is asking "is there anything here
 * to choose between?"
 */
export interface SavedContact {
  id?: string;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  role?: string | null;
}

/** Matching only. What the user typed is what gets stored, always. */
const normaliseAddress = (email: string | null | undefined): string =>
  (email ?? '').trim().toLowerCase();

const agreedValue = (values: Array<string | null | undefined>): string => {
  const present = values.map((v) => (v ?? '').trim()).filter((v) => v !== '');
  if (present.length === 0) return '';
  const first = present[0];
  // Case-insensitive for the AGREEMENT test; the original spelling is returned.
  return present.every((v) => v.toLowerCase() === first.toLowerCase()) ? first : '';
};

/**
 * One entry per distinct address, plus one per addressless row. `length === 1`
 * is the ambiguity test; the entry itself is what to auto-fill.
 */
export function distinctContactCandidates(contacts: SavedContact[]): SavedContact[] {
  const byAddress = new Map<string, SavedContact[]>();
  const addressless: SavedContact[] = [];

  for (const c of contacts) {
    const key = normaliseAddress(c.email);
    if (!key) {
      addressless.push(c);
      continue;
    }
    const group = byAddress.get(key);
    if (group) group.push(c);
    else byAddress.set(key, [c]);
  }

  const collapsed: SavedContact[] = [];
  byAddress.forEach((group) => {
    if (group.length === 1) {
      collapsed.push(group[0]);
      return;
    }
    collapsed.push({
      // The address is the thing the group agrees on — take it from the first
      // row AS TYPED rather than from the normalised key, so a rig mailbox
      // recorded as `Rig2@Caltex.com` is not silently downcased onto the job.
      email: group[0].email,
      name: agreedValue(group.map((c) => c.name)),
      phone: agreedValue(group.map((c) => c.phone)),
      role: agreedValue(group.map((c) => c.role)),
    });
  });

  return [...collapsed, ...addressless];
}
